
import {Component, Mixins, Prop} from 'vue-property-decorator';
import SimpleInputMixin from "@/mixins/SimpleInputMixin.vue";
import Dates from "@/components/inputs/Dates.vue";
import {
  birimSecenekleri,
  dosyaDurumSecenekleri,
  dosyaYilSecenekleri,
  FiltreAnahtar,
  GorunurTakipSonucu,
  kullanilabilirFiltreler
} from "@/components/forms/form-wizard/eicrapro/genelIslemTakipFiltre";

interface FiltreTanimi {
  anahtar: FiltreAnahtar;
  baslik: string;
  ikon: string;
}

/**
 * Genel Islem Sihirbazi 2. adiminin tablo filtresi. (DEV-2718)
 *
 * Eski "Detayli Arama" panelinin yerini alir. Uc davranissal fark var:
 *  1. Yalnizca ISTEMCI TARAFINDA filtreler. Eskiden ayni alanlar hem tabloyu daraltiyor hem
 *     bir sonraki UYAP sorgusunu degistiriyordu; "Sorgula" dugmesi de secimi sessizce siliyordu.
 *     Sunucuya yeniden gitmek isteyen 1. adima doner.
 *  2. Filtre secenekleri YUKLENEN LISTEDEN turetilir. Excel ile gelen satirlarda acilis tarihi
 *     ve dosya durumu bulunmadigi icin o filtreler hic gosterilmez.
 *  3. Her filtre bir cip; uzerinde kac satir eledigi yazar. Sessiz eleme kalmaz.
 */
@Component({
  components: {Dates}
})
export default class TakipTabloFiltre extends Mixins(SimpleInputMixin) {
  @Prop({default: () => []}) takipler!: Array<any>;
  @Prop({required: true}) sonuc!: GorunurTakipSonucu;

  /** Degeri henuz bos olsa da kullanicinin actigi filtreler. */
  elleAcilanlar: Array<FiltreAnahtar> = [];
  /**
   * Tum anahtarlar bastan tanimli: v-model sonradan eklenen bir anahtara yazarsa Vue 2 onu
   * reaktif yapamaz ve cipe tiklandiginda menu acilmaz.
   */
  menuDurumu: Record<string, boolean> = {birim: false, esasNo: false, tarih: false, dosyaDurum: false};

  tanimlar: Array<FiltreTanimi> = [
    {anahtar: 'birim', baslik: 'Mahkeme', ikon: 'mdi-bank-outline'},
    {anahtar: 'esasNo', baslik: 'Esas No', ikon: 'mdi-pound'},
    {anahtar: 'tarih', baslik: 'Açılış Tarihi', ikon: 'mdi-calendar-range'},
    {anahtar: 'dosyaDurum', baslik: 'Dosya Durumu', ikon: 'mdi-file-check-outline'}
  ];

  get kullanilabilirler(): Array<FiltreAnahtar> {
    return kullanilabilirFiltreler(this.takipler);
  }

  get birimSecenekleriListesi() {
    return birimSecenekleri(this.takipler);
  }

  get dosyaDurumSecenekleriListesi() {
    return dosyaDurumSecenekleri(this.takipler);
  }

  get dosyaYilSecenekleriListesi() {
    return dosyaYilSecenekleri(this.takipler).map(yil => String(yil));
  }

  /** Bir filtre ya kullanici actigi icin ya da degeri dolu oldugu icin gorunur. */
  get acikFiltreler(): Array<FiltreAnahtar> {
    return this.kullanilabilirler.filter(
        anahtar => this.elleAcilanlar.indexOf(anahtar) >= 0 || this.cipDoluMu(anahtar)
    );
  }

  get eklenebilirFiltreler(): Array<FiltreTanimi> {
    return this.tanimlar.filter(
        tanim => this.kullanilabilirler.indexOf(tanim.anahtar) >= 0
            && this.acikFiltreler.indexOf(tanim.anahtar) < 0
    );
  }

  get filtreDoluMu(): boolean {
    return this.acikFiltreler.length > 0;
  }

  get toplamElenen(): number {
    return (this.sonuc?.adimlar || []).reduce((toplam, adim) => toplam + adim.elenen, 0);
  }

  get notlar(): Array<string> {
    return (this.sonuc?.adimlar || [])
        .map(adim => adim.not)
        .filter(not => !!not) as Array<string>;
  }

  filtreTanimi(anahtar: FiltreAnahtar): FiltreTanimi {
    return this.tanimlar.find(t => t.anahtar === anahtar) as FiltreTanimi;
  }

  elenenSayisi(anahtar: FiltreAnahtar): number {
    const adim = (this.sonuc?.adimlar || []).find(a => a.anahtar === anahtar);
    return adim ? adim.elenen : 0;
  }

  cipDoluMu(anahtar: FiltreAnahtar): boolean {
    if (anahtar === 'birim')
      return (this.localValue.birimIdler || []).length > 0;
    if (anahtar === 'esasNo')
      return !!this.localValue.dosyaYil || !!this.localValue.dosyaSira;
    if (anahtar === 'tarih')
      return !!this.localValue.baslangicTarihi || !!this.localValue.bitisTarihi;
    return (this.localValue.dosyaDurumlar || []).length > 0;
  }

  /** Coklu secimde ilk secenegin adi + kalanlarin sayisi gosterilir; cip uzamasin. */
  private cokluCipMetni(degerler: Array<string>, secenekler: Array<{ deger: string, metin: string }>): string {
    const ilk = secenekler.find(s => s.deger === String(degerler[0]));
    const ilkMetin = ilk ? ilk.metin : String(degerler[0]);
    return degerler.length > 1 ? ilkMetin + ' +' + (degerler.length - 1) : ilkMetin;
  }

  cipDegeri(anahtar: FiltreAnahtar): string {
    if (!this.cipDoluMu(anahtar))
      return 'tümü';
    if (anahtar === 'birim')
      return this.cokluCipMetni(this.localValue.birimIdler, this.birimSecenekleriListesi);
    if (anahtar === 'esasNo') {
      const yil = this.localValue.dosyaYil || '····';
      if (!this.localValue.dosyaSira)
        return String(yil);
      if (this.localValue.dosyaSira2)
        return yil + '/' + this.localValue.dosyaSira + '–' + this.localValue.dosyaSira2;
      return yil + '/' + this.localValue.dosyaSira;
    }
    if (anahtar === 'tarih')
      return (this.localValue.baslangicTarihi || '…') + ' – ' + (this.localValue.bitisTarihi || '…');
    return this.cokluCipMetni(this.localValue.dosyaDurumlar, this.dosyaDurumSecenekleriListesi);
  }

  filtreEkle(anahtar: FiltreAnahtar) {
    if (this.elleAcilanlar.indexOf(anahtar) < 0)
      this.elleAcilanlar.push(anahtar);
    // Cip DOM'a girdikten sonra menusu acilabilir.
    this.$nextTick(() => this.$set(this.menuDurumu, anahtar, true));
  }

  filtreKaldir(anahtar: FiltreAnahtar) {
    this.elleAcilanlar = this.elleAcilanlar.filter(a => a !== anahtar);
    this.$set(this.menuDurumu, anahtar, false);
    if (anahtar === 'birim')
      this.localValue.birimIdler = [];
    if (anahtar === 'esasNo') {
      this.localValue.dosyaYil = '';
      this.localValue.dosyaSira = '';
      this.localValue.dosyaSira2 = '';
    }
    if (anahtar === 'tarih') {
      this.localValue.baslangicTarihi = '';
      this.localValue.bitisTarihi = '';
    }
    if (anahtar === 'dosyaDurum')
      this.localValue.dosyaDurumlar = [];
    this.input();
  }

  tumFiltreleriTemizle() {
    this.tanimlar.forEach(tanim => this.filtreKaldir(tanim.anahtar));
  }

  /** Sira no temizlenirse aralik alani anlamsiz kalir. */
  onDosyaSiraChange() {
    if (!this.localValue.dosyaSira)
      this.localValue.dosyaSira2 = '';
    this.input();
  }
}
